import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AsyncSelect from "react-select/async";
import type { StylesConfig } from "react-select";
import debounce from "lodash/debounce";
import { RootDispatch, RootState } from "../../store";
import {
  cancelOrder,
  fetchOrdersRoleBased,
  fetchDispatched,
} from "../../store/slices/orderSlice";
import { fetchUserNames } from "../../store/slices/userSlice";
import OrderCard from "./OrderCard";
import toast from "react-hot-toast";
import SearchInput from "../../components/Search/SearchInput";
import PaginationControls from "../../utils/PaginationControls";
import { IOrder } from "../../models/Order.model";
import { useSearchParams } from "react-router-dom";
import DispatchedHistoryTab from "../Warehouse/HomePage/DispatchedHistoryTab";

type UserOption = { value: string; label: string };

const createdBySelectStyles: StylesConfig<UserOption, true> = {
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    borderRadius: 12,
    borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.12)" : "none",
    padding: "2px 4px",
    backgroundColor: "#ffffff",
    transition: "border-color 150ms ease, box-shadow 150ms ease",
    "&:hover": { borderColor: state.isFocused ? "#3b82f6" : "#cbd5e1" },
  }),
  valueContainer: (base) => ({ ...base, gap: 4, padding: "4px 8px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: "#0f172a" }),
  placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 13 }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? "#2563eb" : "#94a3b8",
    "&:hover": { color: "#2563eb" },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "#94a3b8",
    "&:hover": { color: "#e11d48" },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 30,
    marginTop: 6,
    padding: 4,
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
  }),
  menuList: (base) => ({ ...base, padding: 4 }),
  option: (base, state) => ({
    ...base,
    margin: "2px 0",
    padding: "9px 11px",
    borderRadius: 8,
    backgroundColor: state.isSelected
      ? "#eff6ff"
      : state.isFocused
        ? "#f8fafc"
        : "#ffffff",
    color: state.isSelected ? "#1d4ed8" : "#334155",
    fontSize: 13,
    cursor: "pointer",
    "&:active": { backgroundColor: "#dbeafe" },
  }),
  multiValue: (base) => ({
    ...base,
    maxWidth: "100%",
    margin: 2,
    border: "1px solid #dbeafe",
    borderRadius: 999,
    backgroundColor: "#eff6ff",
  }),
  multiValueLabel: (base) => ({
    ...base,
    padding: "3px 5px 3px 9px",
    color: "#1e40af",
    fontSize: 12,
    fontWeight: 500,
  }),
  multiValueRemove: (base) => ({
    ...base,
    margin: 2,
    borderRadius: 999,
    color: "#3b82f6",
    "&:hover": { backgroundColor: "#dbeafe", color: "#1e40af" },
  }),
};

const OrderList: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCreatedBy, setSelectedCreatedBy] = useState<UserOption[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("status") || "pending";
  const createdByIds = useMemo(
    () => selectedCreatedBy.map((user) => user.value),
    [selectedCreatedBy],
  );

  const loadCreatedByOptions = useMemo(
    () =>
      debounce(
        (inputValue: string, callback: (options: UserOption[]) => void) => {
          dispatch(fetchUserNames(inputValue || undefined))
            .unwrap()
            .then((users: Array<{ _id: string; username: string }>) =>
              callback(
                users.map((user) => ({
                  value: user._id,
                  label: user.username,
                })),
              ),
            )
            .catch(() => callback([]));
        },
        300,
      ),
    [dispatch],
  );

  useEffect(() => () => loadCreatedByOptions.cancel(), [loadCreatedByOptions]);

  const {
    pendingOrders,
    approvedOrders,
    cancelledOrders,
    creditedOrders,
    paginations,
    loading,
  } = useSelector((state: RootState) => state.order);

  const handleTabChange = (tabId: string) => {
    setSearchParams({ status: tabId }, { replace: true });
  };
  const fetchOrders = useCallback(
    async (status: string, page: number) => {
      if (status !== "dispatched") {
        await dispatch(
          fetchOrdersRoleBased({
            search: searchTerm,
            status,
            page,
            createdBy: createdByIds.length ? createdByIds : undefined,
          }),
        );
      }
    },
    [createdByIds, dispatch, searchTerm],
  );

  useEffect(() => {
    const status = activeTab;
    const params = { search: searchTerm, page: 1 };
    const run = async () => {
      if (status === "dispatched") {
        await dispatch(fetchDispatched(params));
        return;
      }

      await fetchOrders(status, 1);
    };

    run();
  }, [activeTab, dispatch, fetchOrders, searchTerm]);

  const handlePageChange = (status: string, page: number) => {
    if (status === "dispatched") {
      dispatch(fetchDispatched({ search: searchTerm, page }));
    } else {
      fetchOrders(status, page);
    }
  };

  const handleCancelOrder = async (id: string) => {
    try {
      await dispatch(cancelOrder(id)).unwrap();
    } catch (err) {
      toast.error("Failed to cancel order");
    }
  };

  const tabs = [
    {
      id: "pending",
      label: "Pending Payments",
      styles: {
        active: "border-amber-500 text-amber-600 bg-amber-50",
        badge: "bg-amber-100 text-amber-700",
      },
      count: pendingOrders?.pagination?.totalCount || 0,
      data: pendingOrders,
    },
    {
      id: "credited",
      label: "Credited Orders",
      styles: {
        active: "border-blue-500 text-blue-600 bg-blue-50",
        badge: "bg-blue-100 text-blue-700",
      },
      count: creditedOrders?.pagination?.totalCount || 0,
      data: creditedOrders,
    },
    {
      id: "approved",
      label: "Completed Payments",
      styles: {
        active: "border-emerald-500 text-emerald-600 bg-emerald-50",
        badge: "bg-emerald-100 text-emerald-700",
      },
      count: approvedOrders?.pagination?.totalCount || 0,
      data: approvedOrders,
    },
    {
      id: "cancelled",
      label: "Cancelled Orders",
      styles: {
        active: "border-red-500 text-red-600 bg-red-50",
        badge: "bg-red-100 text-red-700",
      },
      count: cancelledOrders?.pagination?.totalCount || 0,
      data: cancelledOrders,
    },
    {
      id: "dispatched",
      label: "Dispatch History",
      styles: {
        active: "border-sky-500 text-sky-600 bg-sky-50",
        badge: "bg-sky-100 text-sky-700",
      },
      count: paginations.fullyDispatched?.totalCount || 0,
    },
  ];

  const activeTabData = tabs.find((t) => t.id === activeTab);

  return (
    <div className="space-y-5">
      <SearchInput
        placeholder="Search by name, mobile or order number..."
        className="w-full"
        onSearch={setSearchTerm}
      />

      {activeTab !== "dispatched" && (
        <div className="max-w-md">
          <label
            htmlFor="created-by-filter"
            className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500"
          >
            Created by
          </label>
          <AsyncSelect<UserOption, true>
            inputId="created-by-filter"
            cacheOptions
            defaultOptions
            isMulti
            isClearable
            closeMenuOnSelect
            hideSelectedOptions
            loadOptions={loadCreatedByOptions}
            value={selectedCreatedBy}
            onChange={(options) => setSelectedCreatedBy([...options])}
            placeholder="Search users..."
            noOptionsMessage={({ inputValue }) =>
              inputValue ? "No users found" : "Type to search users"
            }
            styles={createdBySelectStyles}
          />
        </div>
      )}

      <div className="overflow-x-auto no-scrollbar">
        <div className="flex gap-2 min-w-max pb-1">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
            flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all duration-200
            text-sm font-medium whitespace-nowrap
            ${
              isActive
                ? `${tab.styles.active} shadow-sm`
                : "border-slate-200 text-slate-600 bg-white hover:bg-slate-50"
            }
          `}
              >
                <span>{tab.label}</span>

                {tab.count !== undefined && tab.count !== 0 && isActive && (
                  <span
                    className={`
              px-2 py-0.5 rounded-full text-xs font-semibold
              ${isActive ? tab.styles.badge : "bg-slate-100 text-slate-600"}
            `}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <section className="animate-in fade-in slide-in-from-bottom-4 duration-500 mt-4">
        {activeTab === "dispatched" ? (
          <DispatchedHistoryTab searchTerm={searchTerm} />
        ) : activeTabData?.data?.data?.length ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 auto-rows-min items-start">
              {activeTabData.data.data.map((order: IOrder) => (
                <OrderCard
                  key={order._id}
                  order={order}
                  searchTerm={searchTerm}
                  activeTab={activeTab}
                  handleCancelOrder={
                    activeTab !== "cancelled"
                      ? () => handleCancelOrder(order._id)
                      : undefined
                  }
                />
              ))}
            </div>
            <PaginationControls
              currentPage={activeTabData.data.pagination.page}
              totalPages={activeTabData.data.pagination.totalPages}
              totalItems={activeTabData.data.pagination.totalCount}
              onPageChange={(p) => handlePageChange(activeTab, p)}
              isLoading={loading}
            />
          </>
        ) : (
          !loading && (
            <div className="flex flex-col items-center justify-center py-20 border border-dashed border-slate-200 rounded-2xl bg-white">
              <div className="text-5xl mb-3">📦</div>

              <h3 className="text-lg font-semibold text-slate-700">
                No Orders Found
              </h3>

              <p className="text-sm text-slate-400 mt-1">
                Try changing search or filters
              </p>
            </div>
          )
        )}
      </section>
    </div>
  );
};

export default OrderList;
